import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {HelpersService} from '../../helpers.service';
import {isNil} from 'lodash-es';
import moment from 'moment';
import {DateAdapter} from "@angular/material/core";
import {AuthenticationService} from "../../../core/authentication.service";
import {SwitchLanguageService} from "../../../core/switch-language/switch-language.service";

/**
 * Component designed to be used as a filter, NOT usable as it is in reactive forms.
 */

@Component({
    selector: 'app-date-range-filter',
    templateUrl: './date-range-filter.component.html',
    styleUrls: ['./date-range-filter.component.scss'],
    standalone: false
})
export class DateRangeFilterComponent implements OnInit {

  @ViewChild('startDateInput', { static: true }) public readonly startDateRef: Input;
  @ViewChild('endDateInput', { static: true }) public readonly endDateRef: Input;

  @Input() startDate: Date;
  @Input() endDate: Date;
  @Input() startDateLabel: string;
  @Input() endDateLabel: string;
  @Input() id = 'date-range-filter';
  @Input() datesNotInOrderError: string;

  @Output() startDateChange = new EventEmitter();
  @Output() endDateChange = new EventEmitter();


  validStartDate = true;
  validEndDate = true;

  minDate = new Date(1);
  maxStartDate = new Date();
  minEndDate = new Date();

  datesInOrder = true;

  constructor(
    private helpersService: HelpersService,
    private dateAdapter: DateAdapter<Date>,
    private authenticationService: AuthenticationService,
    private switchLanguageService: SwitchLanguageService
  ) {
  }

  ngOnInit() {
    this.authenticationService.languageInfo$
      .subscribe(() => {
        this.dateAdapter.setLocale(this.switchLanguageService.getSavedLanguageCode())
      });

    this.dateAdapter.setLocale(this.switchLanguageService.getSavedLanguageCode())
  }


  resetDates(startDate: Date = null, endDate: Date = null) {

    this.minDate = new Date(1);
    this.maxStartDate = new Date();
    this.minEndDate = new Date();

    this.validStartDate = true;
    this.validEndDate = true;
    this.datesInOrder = true;


    this.clearHours(startDate);
    this.setControlValue(this.startDateRef, startDate);

    this.clearHours(endDate);
    this.setControlValue(this.endDateRef, endDate);

    this.setStartDate(startDate);
    this.setEndDate(endDate);
  }


  controlDatesInOrder(): boolean {
    const startDateCtrl: Date = this.getControlValue(this.startDateRef);
    const endDateCtrl: Date =  this.getControlValue(this.endDateRef);

    if (!this.isValidDate(this.startDateRef) || !this.isValidDate(this.endDateRef)) {
      return false;
    }

    if(!isNil(startDateCtrl) && !isNil(endDateCtrl)) {
      //we care about order
      this.datesInOrder =  startDateCtrl <= endDateCtrl;
    } else {
     this.datesInOrder = true;
    }
    return this.datesInOrder;
  }


  checkDatesInOrder() {
    if(this.validStartDate && this.validEndDate && !isNil(this.startDate) && !isNil(this.endDate)) {
      //we care about order
      this.datesInOrder = this.startDate < this.endDate;
    } else {
      this.datesInOrder = true;
    }
  }


  setStartDate($event: any) {
    if($event instanceof Date) {
      //from picker
      this.startDateRef['nativeElement'].value = moment($event).format('MM/DD/YYYY');
    }
    this.validStartDate = this.isValidDate(this.startDateRef);
    if (this.validStartDate && this.controlDatesInOrder()) {
      this.startDate = $event;
      this.minEndDate = this.startDate;
      this.startDateChange.emit(this.startDate);


      const endDateControl = this.getControlValue(this.endDateRef);

      if(endDateControl?.getTime() !== this.endDate?.getTime()) {
        this.setEndDate(endDateControl);
      }
    }
  }

  setEndDate($event: any) {
    if($event instanceof Date) {
      //from picker
      this.endDateRef['nativeElement'].value = moment($event).format('MM/DD/YYYY');
    }
    this.validEndDate = this.isValidDate(this.endDateRef)
    if (this.validEndDate && this.controlDatesInOrder()) {
      this.endDate = $event;
      this.maxStartDate = this.endDate;
      this.endDateChange.emit(this.endDate);

      const startDateControl = this.getControlValue(this.startDateRef);

      if(startDateControl?.getTime() !== this.startDate?.getTime()) {
        this.setStartDate(startDateControl);
      }
    }
  }

  private isValidDate(dateInput: Input) {
    const inputValue = dateInput['nativeElement'].value;
    return this.helpersService.isValidDate(inputValue);
  }

  private getControlValue(dateInput: Input): Date {
    const controlString = dateInput['nativeElement'].value;
    return !isNil(controlString) && controlString !== '' ? moment(controlString).toDate() : null;
  }


  private setControlValue(dateRef: Input, date: Date) {
    if(!isNil(date)) {
      dateRef['nativeElement'].value = moment(date).format('MM/DD/YYYY');
    } else {
      dateRef['nativeElement'].value = null;
    }
  }

  private clearHours(date: Date) {
    date?.setHours(0,0,0,0);
  }

}
